import { getUiLocale } from '../../../utils/uiTranslation';
import React, { useState, useImperativeHandle, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL_PROD;

const NotesSection = forwardRef(({ employeeId, companyUID, notes, currentUser, onAddNote, loading }, ref) => {
  const { t } = useTranslation('myteam');
  const [newNote, setNewNote] = useState('');
  const [adding, setAdding] = useState(false);
  const [displayLimit, setDisplayLimit] = useState(4);
  const textareaRef = React.useRef(null);
  const sectionRef = React.useRef(null);

  // Expose focus method to parent
  useImperativeHandle(ref, () => ({
    focus: () => {
      if (sectionRef.current) {
        sectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => {
          if (textareaRef.current) {
            textareaRef.current.focus();
            // Highlight briefly
            textareaRef.current.style.borderColor = 'rgba(86, 141, 237, 0.8)';
            textareaRef.current.style.backgroundColor = 'var(--app-surface, rgba(86, 141, 237, 0.15))';
            setTimeout(() => {
              if (textareaRef.current) {
                textareaRef.current.style.borderColor = 'rgba(86, 141, 237, 0.3)';
                textareaRef.current.style.backgroundColor = 'var(--app-surface, rgba(86, 141, 237, 0.1))';
              }
            }, 1000);
          }
        }, 300);
      }
    }
  }));

  const handleSubmit = async () => {
    if (!newNote.trim()) return;
    setAdding(true);

    try {
      if (!currentUser) throw new Error('Authentication required');
      const token = await currentUser.getIdToken();
      const response = await fetch(`${BACKEND_URL}/employee/add-note`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          companyUID,
          employeeId,
          // The server writes the author from the verified token.
          note: newNote
        })
      });

      const result = await response.json();
      if (result.success) {
        setNewNote('');
        onAddNote?.();
      } else {
        alert(t('notes.addErrorWithReason', {
          message: result.error || t('notes.addError', 'Failed to add note'),
          defaultValue: `Failed to add note: ${result.error || 'Unknown error'}`
        }));
      }
    } catch (error) {
      console.error('Add note failed:', error);
      alert(t('notes.addError', 'Failed to add note'));
    } finally {
      setAdding(false);
    }
  };

  return (
    <div ref={sectionRef}>
      <div style={{ marginBottom: 20 }}>
        <textarea
          ref={textareaRef}
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
          placeholder={t('notes.placeholder', 'Add a note about this employee...')}
          disabled={adding}
          style={{
            width: '100%',
            minHeight: 80,
            fontFamily: 'Montserrat',
            fontSize: '14px',
            color: 'var(--app-text, #FFFFFF)',
            backgroundColor: 'var(--app-surface, rgba(86, 141, 237, 0.1))',
            border: "1px solid var(--app-border-strong, rgba(86, 141, 237, 0.3))",
            borderRadius: 8,
            padding: 12,
            resize: 'vertical',
            marginBottom: 8,
            transition: 'all 0.3s ease'
          }}
          className="notes-textarea"
        />
        <button
          onClick={handleSubmit}
          disabled={adding || !newNote.trim()}
          style={{ '--light-control-fill': adding || !newNote.trim() ? 'var(--app-surface, rgba(86, 141, 237, 0.2))' : '#568ded',
            padding: '10px 20px',
            backgroundColor: adding || !newNote.trim() ? 'var(--app-surface, rgba(86, 141, 237, 0.2))' : '#568ded',
            border: 'none',
            borderRadius: 6,
            color: adding || !newNote.trim() ? 'var(--app-text, #FFFFFF)' : "#FFFFFF",
            fontFamily: 'Montserrat',
            fontSize: '14px',
            fontWeight: 600,
            cursor: adding || !newNote.trim() ? 'not-allowed' : 'pointer'
          }}
          data-light-control={adding || !newNote.trim() ? "quiet" : "filled"}>
          {adding ? t('notes.adding', 'Adding...') : t('notes.add', 'Add Note')}
        </button>
      </div>

      {loading ? (
        <div style={{ color: 'var(--app-text, #FFFFFF)', opacity: 0.7 }}>{t('notes.loading', 'Loading notes...')}</div>
      ) : !notes || notes.length === 0 ? (
        <div style={{ color: 'var(--app-text, #FFFFFF)', opacity: 0.7 }}>{t('notes.empty', 'No notes yet.')}</div>
      ) : (
        <>
          {notes.slice(0, displayLimit).map((note, idx) => (
            <div key={note.id || idx} style={{
              backgroundColor: 'var(--app-surface, rgba(86, 141, 237, 0.08))',
              border: "1px solid var(--app-border, rgba(86, 141, 237, 0.15))",
              borderRadius: 12,
              padding: 16,
              marginBottom: 12
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: 8
              }}>
                <span style={{
                  fontFamily: 'Montserrat',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: 'var(--app-text, #FFFFFF)'
                }}>
                  {note.authorName}
                </span>
                <span style={{
                  fontFamily: 'Montserrat',
                  fontSize: '12px',
                  color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.6)'
                }}>
                  {new Date(note.timestamp).toLocaleString(getUiLocale(), { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
              <div style={{
                fontFamily: 'Montserrat',
                fontSize: '14px',
                color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.9)',
                lineHeight: 1.5
              }}>
                {note.text}
              </div>
            </div>
          ))}

          {/* Load More Button */}
          {notes.length > displayLimit && (
            <div style={{ marginTop: '16px', textAlign: 'center' }}>
              <button
                onClick={() => setDisplayLimit(prev => prev + 10)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#568ded',
                  fontFamily: 'Montserrat',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  padding: 0
                }}
                data-light-control={"quiet"}>
                {t('common:buttons.more', 'Load More')}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
});

NotesSection.displayName = 'NotesSection';

export default NotesSection;

